
import { useAnalytics } from '@/hooks/useAnalytics';

// Event types based on the tag list
export type EventName = 
  | 'page_view'
  | 'cta_click'
  | 'form_started'
  | 'form_submission'
  | 'popup_shown'
  | 'popup_closed'
  | 'newsletter_signup'
  | 'cookie_consent_given'
  | 'scroll_depth'
  | 'article_click'
  | 'tab_switch'
  | 'faq_toggle'
  | 'pending_contacts_found'
  | 'contact_recovery_started'
  | 'contact_recovery_success'
  | 'contact_recovery_zero'
  | 'error';

// Helper functions for tracking specific events
export const trackingHelpers = {
  /**
   * Track a CTA button click
   */
  trackCta: (label: string, location?: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('cta_click', { 
      label, 
      location: location || window.location.pathname 
    });
  },

  /**
   * Track form field interaction
   */
  trackFormStarted: (formName: string, fieldName: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('form_started', { 
      form: formName,
      field: fieldName,
      location: window.location.pathname 
    });
  },

  /**
   * Track successful form submission
   */
  trackFormSubmission: (formName: string, marketingConsent: boolean, additionalData?: Record<string, any>) => {
    const { trackEvent, trackFormSubmission } = useAnalytics();
    
    // Use the dedicated form tracking function
    trackFormSubmission(formName, {
      marketing_consent: marketingConsent,
      ...additionalData
    });
    
    // Also log as regular event for redundancy
    trackEvent('form_submission', {
      form: formName,
      marketing_consent: marketingConsent,
      ...additionalData
    });
  },

  /**
   * Track popup visibility
   */
  trackPopupShown: (popupType: string, delay?: number) => {
    const { trackEvent } = useAnalytics();
    trackEvent('popup_shown', {
      popup_type: popupType,
      delay: delay || 0,
      location: window.location.pathname
    });
  },

  /**
   * Track popup closure
   */
  trackPopupClosed: (popupType: string, reason: 'manual' | 'auto' | 'conversion') => {
    const { trackEvent } = useAnalytics();
    trackEvent('popup_closed', {
      popup_type: popupType,
      close_reason: reason,
      location: window.location.pathname
    });
  },

  /**
   * Track newsletter signup
   */
  trackNewsletterSignup: (method: 'popup' | 'footer' | 'contact' | 'article' | 'sidebar', source?: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('newsletter_signup', {
      method,
      source: source || window.location.pathname,
      location: window.location.pathname
    });
  },

  /**
   * Track cookie consent selection
   */
  trackCookieConsent: (consentType: 'all' | 'necessary_only' | 'rejected') => {
    const { trackEvent } = useAnalytics();
    trackEvent('cookie_consent_given', {
      consent_type: consentType,
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track scroll depth on articles and long pages
   */
  trackScrollDepth: (depth: number, pageType: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('scroll_depth', {
      page: window.location.pathname,
      page_type: pageType,
      depth
    });
  },

  /**
   * Track article link clicks
   */
  trackArticleClick: (articleId: string | number, category: string, title: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('article_click', {
      article_id: articleId,
      category,
      title,
      source: window.location.pathname
    });
  },

  /**
   * Track tab switching in comparison tools
   */
  trackTabSwitch: (tabName: string, componentName: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('tab_switch', {
      tab: tabName,
      component: componentName,
      location: window.location.pathname
    });
  },

  /**
   * Track FAQ interaction
   */
  trackFaqToggle: (questionId: string, action: 'open' | 'close', question: string) => {
    const { trackEvent } = useAnalytics();
    trackEvent('faq_toggle', {
      question_id: questionId,
      action,
      question,
      location: window.location.pathname
    });
  },

  /**
   * Track errors
   */
  trackError: (context: string, message: string, code?: string | number) => {
    const { trackEvent } = useAnalytics();
    trackEvent('error', {
      context,
      message,
      code,
      location: window.location.pathname,
      timestamp: new Date().toISOString()
    });
  }
};

// Create a custom hook to expose all tracking helpers
export const useTracking = () => {
  return { ...trackingHelpers };
};
